import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { SpinningLoader, Text } from '@frontend/design-system';

type Props = CommonHTMLAttributes & {
  value: number;
  max: number;
};
export const DraftSizeProgressBar = ({ value, max, ...rest }: Props) => {
  const { t } = useTranslation('fax');
  return (
    <div
      css={css`
        label {
          progress {
            width: 100%;
            display: block;
          }
        }
      `}
      {...rest}
    >
      <label>
        <div css={[progressBarStyle(value)]}>
          <progress value={value} max={max} css={{ visibility: 'hidden', height: 0, width: 0 }} />
        </div>
        <Text size='small' color='subdued' textAlign='center'>
          {t(`{{value}} / {{max}} MB (large files may have a hard time sending)`, { value: value.toPrecision(2), max })}
        </Text>
        <Text size='small' color='subdued' textAlign='center'>
          {t('200 page limit')}
        </Text>
      </label>
    </div>
  );
};

type LoadingProps = CommonHTMLAttributes;
export const DraftSizeProgressLoading = (props: LoadingProps) => {
  const { t } = useTranslation('fax');

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${theme.spacing(1)};
      `}
      {...props}
    >
      {t('Calculating Size')} <SpinningLoader size='xs' />
    </div>
  );
};

const progressBarStyle = (progress: number) =>
  css({
    position: 'relative',
    height: theme.spacing(0.5),
    background: theme.colors.neutral20,
    borderRadius: theme.borderRadius.medium,
    marginBottom: theme.spacing(1),
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: `${progress * 2}%`,
      background: theme.colors.primary30,
      borderRadius: theme.borderRadius.medium,
    },
    '@media only screen and (max-width: 600px)': {
      height: theme.spacing(0.5),
    },
  });
